import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@repo/ping-react-js";
import { Plus, ShieldCheck, Building2, Users, Search } from "lucide-react";
import {
  fetchOrgCreds,
  setOrgCreds,
  deleteOrgCreds,
  fetchCredentialSources,
  type CredEntry,
} from "../../api/credentials";
import { fetchMe, type MeResponse } from "../../api/me";
import { fetchOrgUsers, type AdminUser } from "../../api/admin";
import CredentialEditModal from "../../components/CredentialEditModal";
import CredentialCardGrid from "../../components/CredentialCardGrid";
import "./OrgSettingsPage.scss";

type ModalState =
  | { type: "none" }
  | { type: "edit"; source: string; secretTypes: string[] }
  | { type: "add" };

const OrgSettingsPage = () => {
  const { shortName } = useParams<{ shortName: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [creds, setCreds] = useState<CredEntry[]>([]);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [canManage, setCanManage] = useState(false);
  const [organizationCredentialManagementEnabled, setOrganizationCredentialManagementEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [tab, setTab] = useState<"credentials" | "users">("credentials");
  const [modal, setModal] = useState<ModalState>({ type: "none" });
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [usersQuery, setUsersQuery] = useState("");
  const [sourceNameMap, setSourceNameMap] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!accessToken || !shortName) return;
    setLoading(true);
    setError(null);
    try {
      const meResult = await fetchMe(accessToken);
      setMe(meResult);

      const userOrgShort = (meResult.organization?.short_name ?? "").toUpperCase();
      const targetShort = shortName.toUpperCase();

      if (!meResult.is_staff && targetShort !== userOrgShort) {
        setError("You are not a member of this organization.");
        setLoading(false);
        return;
      }

      const credsResult = await fetchOrgCreds(accessToken, shortName);

      setCreds(credsResult.credentials);
      setCanManage(credsResult.can_manage);
      setOrganizationCredentialManagementEnabled(credsResult.organization_credential_management_enabled !== false);
      setOrgName(credsResult.organization.name);

      const sourcesResult = await fetchCredentialSources(accessToken)
        .catch(() => ({ sources: [] }));
      setSourceNameMap(
        Object.fromEntries(
          sourcesResult.sources.flatMap((s) => [[s.code, s.member_code ?? s.code], ...(s.member_code ? [[s.member_code, s.member_code]] : [])]),
        ),
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [accessToken, shortName]);

  useEffect(() => {
    if (shortName?.toLowerCase() === "ping-global") {
      navigate("/org/ping-global", { replace: true });
      return;
    }
    load();
  }, [load, navigate, shortName]);

  useEffect(() => {
    if (!loading && !organizationCredentialManagementEnabled) {
      navigate("/", { replace: true });
    }
  }, [loading, organizationCredentialManagementEnabled, navigate]);

  const handleSave = async (source: string, fields: Record<string, string>) => {
    if (!accessToken || !shortName) return;
    setSaving(source);
    setSaveError(null);
    try {
      await setOrgCreds(accessToken, shortName, source, fields);
      await load();
      setModal({ type: "none" });
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : "Failed to save credentials");
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (source: string) => {
    if (!accessToken || !shortName) return;
    setSaving(source);
    setSaveError(null);
    try {
      await deleteOrgCreds(accessToken, shortName, source);
      await load();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : "Failed to remove credentials");
    } finally {
      setSaving(null);
    }
  };

  const configuredSources = creds.sort((a, b) => a.source.localeCompare(b.source));

  const loadUsers = async (query?: string) => {
    if (!accessToken || !shortName) return;
    setUsersLoading(true);
    setUsersError(null);
    try {
      const result = await fetchOrgUsers(accessToken, shortName, query);
      setUsers(result.users);
    } catch (err: unknown) {
      setUsersError(err instanceof Error ? err.message : "Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "users" && me?.is_staff) {
      loadUsers(usersQuery);
    }
  }, [tab, me?.is_staff, shortName]);

  if (!shortName || !me) {
    return (
      <>
        <header className="app-header">
          <h1>Org Settings</h1>
        </header>
        <div className="app-content">
          {loading && <p>Loading...</p>}
          {error && <p className="creds-status creds-error">{error}</p>}
        </div>
      </>
    );
  }

  return (
    <>
      <header className="app-header">
        <div className="creds-header">
          <h1>{orgName ?? shortName}</h1>
          <div className="creds-user-org">
            <Building2 size={14} />
            <span>{orgName ?? shortName} ({shortName.toUpperCase()})</span>
          </div>
        </div>
      </header>

      <div className="org-settings-tabs">
        <button
          className={`org-settings-tab ${tab === "credentials" ? "active" : ""}`}
          onClick={() => setTab("credentials")}
        >
          <ShieldCheck size={14} />
          Credentials
        </button>
        <button
          className={`org-settings-tab ${tab === "users" ? "active" : ""}`}
          onClick={() => setTab("users")}
        >
          <Users size={14} />
          Users
        </button>
      </div>

      <div className="app-content">
        {loading && <p className="creds-status">Loading...</p>}
        {error && <p className="creds-status creds-error">{error}</p>}

        {!loading && !error && tab === "credentials" && (
          <>
            {!canManage ? (
              <p className="creds-status creds-error">
                You need organization admin access to manage credentials.
              </p>
            ) : (
              <>
                <div className="creds-header-actions" style={{ marginBottom: 16 }}>
                  <button
                    className="apikeys-create-btn"
                    onClick={() => {
                      setSaveError(null);
                      setModal({ type: "add" });
                    }}
                  >
                    <Plus size={16} />
                    Add Integration
                  </button>
                </div>

                {configuredSources.length === 0 ? (
                  <p className="creds-status">
                    No credentials configured for this organization.
                  </p>
                ) : (
                  <CredentialCardGrid
                    entries={configuredSources}
                    sourceNameMap={sourceNameMap}
                    saving={saving}
                    onEdit={(source, secretTypes) => {
                      setSaveError(null);
                      setModal({ type: "edit", source, secretTypes });
                    }}
                    onDelete={handleDelete}
                  />
                )}
              </>
            )}
          </>
        )}

        {!loading && !error && tab === "users" && (
          <>
            {!me.is_staff ? (
              <p className="creds-status">Staff access required to view users.</p>
            ) : (
              <>
                <div className="org-settings-search-wrap">
                  <Search size={16} />
                  <input
                    className="org-settings-search-input"
                    type="text"
                    placeholder={`Users in ${shortName.toUpperCase()}...`}
                    value={usersQuery}
                    onChange={(e) => {
                      const val = e.target.value;
                      setUsersQuery(val);
                      loadUsers(val || undefined);
                    }}
                  />
                </div>
                {usersLoading && <p className="creds-status">Loading users...</p>}
                {usersError && <p className="creds-status creds-error">{usersError}</p>}
                {!usersLoading && !usersError && users.length === 0 && (
                  <p className="creds-status">No users found in this organization.</p>
                )}
                {users.length > 0 && (
                  <div className="org-settings-users">
                    {users.map((u) => (
                      <div key={u.username} className="org-settings-user-item">
                        <div className="org-settings-user-main">
                          <Users size={16} />
                          <strong>{u.username}</strong>
                          {u.is_staff && <span className="org-settings-user-badge">Staff</span>}
                          {!u.is_active && <span className="org-settings-user-badge inactive">Inactive</span>}
                        </div>
                        <div className="org-settings-user-side">
                          {u.email && (
                            <div className="org-settings-user-email">
                              <span>{u.email}</span>
                            </div>
                          )}
                          <button
                            className="org-settings-user-creds-btn"
                            onClick={() => navigate(`/user/${u.id}/credentials`)}
                            title="Manage credentials"
                          >
                            <ShieldCheck size={14} />
                            Credentials
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {(modal.type === "edit" || modal.type === "add") && (
        <CredentialEditModal
          source={modal.type === "edit" ? modal.source : ""}
          saving={saving === (modal.type === "edit" ? modal.source : "__new__")}
          title={modal.type === "edit" ? `Edit ${modal.source}` : "Add Integration"}
          existingSources={configuredSources.map((c) => c.source)}
          secretTypes={modal.type === "edit" ? modal.secretTypes : []}
          saveError={saveError}
          onSave={(source, fields) => handleSave(source, fields)}
          onClose={() => setModal({ type: "none" })}
        />
      )}
    </>
  );
};

export default OrgSettingsPage;
